import { useApiErrorHandler } from '@/account/hook/useApiErrorHandler';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { type OrderTableRow, loadOrdersForTable, orderTableQueryKey } from '@/api/registration';
import { type ApiResponse } from '@/api/tenantClient';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import { uiStyles } from '@/lib/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useCallback, useMemo } from 'react';
import Datatable from './Datatable';
import BaseLoadingSpinner from '@/base/Loading/Spinner';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { type MRT_ColumnFiltersState, type MRT_PaginationState, type MRT_SortingState, type MRT_VisibilityState } from 'mantine-react-table';
import { useSavedViews } from '@/lib/hook/useSavedViews';
import { type TableViewConfig } from '@/api/common/tableViews';

export interface SerializedOrderTableRow extends Omit<OrderTableRow, 'purchasedAt'> {
  purchasedAt: Date | null
}

interface Params {
  filters: any
  sorting: any
  pagination: any
  search?: string
}

const useGetOrders = ({ filters, sorting, pagination, search }: Params & { setPagination: React.Dispatch<React.SetStateAction<any>> }) => {
  const { createTenantAdminApiRequest, isLoading: isTenantLoading } = useTenantApi();

  return useQuery<ApiResponse<OrderTableRow>, Error>({
    queryKey: [orderTableQueryKey, pagination, filters, sorting, search],
    queryFn: async (): Promise<ApiResponse<OrderTableRow>> => {
      const response = await loadOrdersForTable(createTenantAdminApiRequest)({
        page: pagination.pageIndex,
        size: pagination.pageSize,
        filters,
        sorting,
        search,
      });
      return response;
    },
    enabled: !isTenantLoading,
    keepPreviousData: true,
    staleTime: 30_000
  });
};

const initialVisibilityState: MRT_VisibilityState = {
  tickets: false,
  purchasedAtDate: false,
  purchasedAtTime: false,
  'delegateType.name': false,
  'ticket.workshop': false,
  'ticket.tour': false,
  'ticket.function': false,
  invoiceNumber: false,
  paymentTransactionId: false,
  paymentTransactionSuccessful: true,
  delegateType: false,
  'attendeeCategory.name': false,
  discountCode: false,
  country: false,
  state: false,
  paymentOption: false,
  offlinePaymentType: false,
  createdAtDate: false,
  createdAtTime: false,
  userId: false,
  userCompany: false,
};

const LOCAL_TABLE_STORAGE_KEY = 'order_table';
const defaultPaginationState: MRT_PaginationState = { pageIndex: 0, pageSize: 10 };

const OrderTable = () => {
  const { tenantCode } = useRegisterRoute();
  const [filters, setFilters] = useLocalStorage<MRT_ColumnFiltersState>(`${LOCAL_TABLE_STORAGE_KEY}_filters_${tenantCode}`, []);
  const [sorting, setSorting] = useLocalStorage<MRT_SortingState>(`${LOCAL_TABLE_STORAGE_KEY}_sorting_${tenantCode}`, []);
  const [search, setSearch] = useLocalStorage<string>(`${LOCAL_TABLE_STORAGE_KEY}_search_${tenantCode}`, '');
  const [pagination, setPagination] = useLocalStorage<MRT_PaginationState>(`${LOCAL_TABLE_STORAGE_KEY}_pagination_${tenantCode}`, defaultPaginationState);
  const [columnVisibility, setColumnVisibility] = useLocalStorage<MRT_VisibilityState>(`${LOCAL_TABLE_STORAGE_KEY}_visibility_${tenantCode}`, initialVisibilityState);
  const { reportToGlobal } = useApiErrorHandler();

  const handleApplyView = useCallback((config: TableViewConfig) => {
    const nextFilters = (config.columnFilters ?? []).map(filter => {
      const value = filter.value;
      let clonedValue = value;

      if (Array.isArray(value)) {
        clonedValue = value.map(entry => {
          return entry instanceof Date ? new Date(entry) : entry;
        });
      } else if (value instanceof Date) {
        clonedValue = new Date(value);
      }

      return {
        ...filter,
        value: clonedValue
      };
    });

    setFilters(nextFilters);
    setSorting(config.sorting ? [...config.sorting] : []);
    setColumnVisibility(config.columnVisibility ? { ...config.columnVisibility } : { ...initialVisibilityState });
    setSearch(config.globalFilter ?? '');
    setPagination(config.pagination ? { ...config.pagination } : { ...defaultPaginationState });
  }, [setFilters, setSorting, setColumnVisibility, setSearch, setPagination]);

  const defaultViewConfig = useMemo<TableViewConfig>(() => {
    return {
      columnFilters: [],
      sorting: [],
      columnVisibility: { ...initialVisibilityState },
      columnOrder: [],
      globalFilter: '',
      pagination: { ...defaultPaginationState },
    };
  }, []);

  const savedViews = useSavedViews({
    tableIdentifier: 'orders',
    currentConfig: {
      columnFilters: filters,
      sorting,
      columnVisibility,
      globalFilter: search,
      pagination,
    },
    onApplyView: handleApplyView,
    defaultConfig: defaultViewConfig
  });

  const { data, isLoading, error, isError, isFetching, refetch } = useGetOrders({ filters, sorting, pagination, search, setPagination });
  useEffect(
    () => {
      if (error) {
        reportToGlobal(error);
      }
    },
    [error]
  );

  if (!data || isLoading) {
    return (
      <BaseLoadingSpinner />
    );
  }

  if (data.items && Array.isArray(data.items)) {
    return (
      <Datatable
        data={data.items.map((order) => {
          return {
            ...order,
            purchasedAt: order.purchasedAt === null ? null : new Date(order.purchasedAt)
          };
        })}
        isLoading={isLoading}
        isError={isError}
        isFetching={isFetching}
        refetch={refetch}
        filters={filters}
        setFilters={setFilters}
        sorting={sorting}
        setSorting={setSorting}
        search={search}
        setSearch={setSearch}
        pagination={pagination}
        setPagination={setPagination}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        rowCount={data.pagination?.total ?? 0}
        paymentOptions={data.selectOptions?.paymentOptions ?? []}
        eventSelectOptions={data.selectOptions?.eventSelectOptions ?? []}
        statusSelectOptions={data.selectOptions?.statusSelectOptions ?? []}
        workshopTicketsSelectOptions={data.selectOptions?.workshopTicketsSelectOptions ?? []}
        functionTicketsSelectOptions={data.selectOptions?.functionTicketsSelectOptions ?? []}
        tourTicketsSelectOptions={data.selectOptions?.tourTicketsSelectOptions ?? []}
        delegateTypeSelectOptions={data.selectOptions?.delegateTypeSelectOptions ?? []}
        savedViews={savedViews}
      />
    );
  } else {
    return (
      <BaseMessageBarInfo borderRadius={uiStyles.borderRadius}>
        No orders
      </BaseMessageBarInfo>
    );
  }
};

export default OrderTable;
